import * as React from "react"
import { RadioButtonFieldBlock, Spacer, Text, ThemeCss } from "gatsby-interface"
import { PreviewTriggerType } from "@modules/graphql/types"
import { useUpdatePreviewTriggerTypeMutation } from "../queries.generated"
import {
  SitePreviewSettingsFieldsFragment,
  SitePreviewSettingsFieldsFragmentDoc,
} from "../fragments.generated"
import { sitePreviewSettings as text } from "@modules/locales/default.js"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"

export type PreviewUpdateTypeConfigurationProps = {
  siteId: string
  previewTriggerType: PreviewTriggerType
}

export function PreviewUpdateTypeConfiguration({
  siteId,
  previewTriggerType,
}: PreviewUpdateTypeConfigurationProps) {
  const [updatePreviewTriggerType] = useUpdatePreviewTriggerType(siteId)
  const [setError, errorAlert] = useTriggerErrorAlert()

  return (
    <React.Fragment>
      <RadioButtonFieldBlock
        id="previewUpdateType"
        name="previewUpdateType"
        label={text.labels.previewUpdateType}
        value={previewTriggerType}
        onChange={e => {
          const nextTriggerType = e.target.value as PreviewTriggerType
          updatePreviewTriggerType(nextTriggerType).catch(err => setError(err))
        }}
        options={[
          {
            value: PreviewTriggerType.Automatic,
            label: (
              <PreviewUpdateTypeLabel
                title={text.labels.previewUpdateTypeOptionAuto}
                description={
                  text.messages.previewUpdateTypeOptionAutoDescription
                }
              />
            ),
          },
          {
            value: PreviewTriggerType.Manual,
            label: (
              <PreviewUpdateTypeLabel
                title={text.labels.previewUpdateTypeOptionManual}
                description={
                  text.messages.previewUpdateTypeOptionManualDescription
                }
              />
            ),
          },
        ]}
      />
      {errorAlert && (
        <React.Fragment>
          <Spacer size={5} />
          {errorAlert}
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

function useUpdatePreviewTriggerType(siteId: string) {
  const [mutate, mutationInfo] = useUpdatePreviewTriggerTypeMutation()

  const updatePreviewTriggerType = (triggerType: PreviewTriggerType) => {
    return mutate({
      variables: {
        siteId,
        triggerType: triggerType,
      },
      optimisticResponse: {
        updatePreviewTriggerType: {
          success: true,
          __typename: `MutationResult`,
        },
      },
      update: proxy => {
        const fragmentInfo = {
          id: `Site:${siteId}`,
          fragment: SitePreviewSettingsFieldsFragmentDoc,
        }
        const fragmentData = proxy.readFragment<
          SitePreviewSettingsFieldsFragment
        >(fragmentInfo)

        proxy.writeFragment<SitePreviewSettingsFieldsFragment>({
          ...fragmentInfo,
          data: {
            ...fragmentData,
            previewTriggerType: triggerType,
          },
        })
      },
    })
  }

  return [updatePreviewTriggerType, mutationInfo] as const
}

const labelCss: ThemeCss = theme => ({
  color: theme.colors.text.secondary,
})

const labelTitleCss: ThemeCss = theme => ({
  fontWeight: theme.fontWeights.semiBold,
  color: theme.colors.text.primary,
})

type PreviewUpdateTypeLabelProps = {
  title: string
  description: string
}

function PreviewUpdateTypeLabel({
  title,
  description,
}: PreviewUpdateTypeLabelProps) {
  return (
    <Text as="span" size="S" css={labelCss}>
      <strong css={labelTitleCss}>{title}</strong>
      <br />
      {description}
    </Text>
  )
}
